import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_CHANNELS = ["instagram", "facebook", "linkedin", "email", "print", "in_person"];

export const SuggestionsCard = ({ authHeader, refreshKey = 0, onLogged }) => {
  const [items, setItems] = useState([]);
  const [channels, setChannels] = useState({});
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bdm/suggestions`, { headers: authHeader() });
      setItems(data.items || []);
      setChannels(data.channels || {});
    } catch {
      setItems([]);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const confirm = async (item, channel) => {
    setBusy(item.id);
    try {
      await axios.post(
        `${API}/bdm/suggestions/${item.id}/confirm`,
        { channel },
        { headers: authHeader() },
      );
      toast.success("Logged — that's one less thing at month end");
      setOpenId(null);
      load();
      onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log that.");
    } finally {
      setBusy("");
    }
  };

  const dismiss = async (item) => {
    setBusy(item.id);
    try {
      await axios.delete(`${API}/bdm/suggestions/${item.id}`, { headers: authHeader() });
      load();
    } finally {
      setBusy("");
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="bg-lime/15 border border-lime rounded-3xl shadow-soft p-6" data-testid="bdm-suggestions-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <Sparkles size={13} /> Quick catch-up
      </div>
      <h3 className="mt-1 font-heading font-medium text-xl text-navy">
        {items.length} you grabbed but haven't logged
      </h3>
      <p className="mt-1 text-sm text-ink-500">
        We only know you downloaded these — you tell us if you actually used them. One tap and it's on
        your record.
      </p>

      <div className="mt-4 space-y-2">
        {items.map((it) => (
          <div
            key={it.id}
            className="rounded-2xl bg-white border border-linen-border p-3"
            data-testid={`bdm-suggestion-${it.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-navy truncate" title={it.asset_filename}>
                  {it.asset_filename || "Marketing asset"}
                </p>
                <p className="text-[11px] text-ink-400">
                  Downloaded {(it.last_grabbed_at || "").slice(0, 10)}
                  {it.folder_name ? ` · ${it.folder_name}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => dismiss(it)}
                disabled={busy === it.id}
                data-testid={`bdm-suggestion-dismiss-${it.id}`}
                title="I didn't use this"
                className="text-ink-400 hover:text-red-600 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {openId === it.id ? (
              <div className="mt-3">
                <p className="text-[11px] uppercase tracking-widest text-ink-500">Where did it go?</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {QUICK_CHANNELS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => confirm(it, c)}
                      disabled={busy === it.id}
                      data-testid={`bdm-suggestion-channel-${it.id}-${c}`}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
                    >
                      {channels[c] || c}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setOpenId(it.id)}
                data-testid={`bdm-suggestion-confirm-${it.id}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-lime text-navy hover:brightness-95"
              >
                Yes, I used this
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SuggestionsCard;
